import React, { useState, useRef, useEffect } from 'react';
import { toastMsg } from './toast';


/**
 * InlineTitleEditor
 *
 * A Google Docs-style inline text editor.
 *
 * @param {string} initialTitle - The starting title.
 * @param {string} defaultTitle - The fallback title if the user leaves the input blank (e.g., "Untitled Quiz").
 * @param {function} onSave - Callback function triggered when the title is successfully updated. Returns the new title.
 * @param {string} className - Wrapper container class names.
 * @param {string} textClassName - Class names applied to the idle text element.
 * @param {string} inputClassName - Class names applied to the active input element.
 */
export default function InlineTitleEditor({
  initialTitle = '',
  defaultTitle = 'Untitled Quiz',
  onSave = () => {},
  className = '',
  textClassName = '',
  inputClassName = ''
}) {
 
  // Controls whether we are showing the text or the input field
  const [isEditing, setIsEditing] = useState(false);
 
  // The 'official' saved title
  const [currentTitle, setCurrentTitle] = useState(initialTitle || defaultTitle);
 
  // The temporary title being typed in the input field
  const [tempTitle, setTempTitle] = useState(currentTitle);
 
  // Reference to the input element to manage focus and text selection
  const inputRef = useRef(null);

  // Set when Escape is pressed so the blur that follows discards instead of saving
  const skipSaveRef = useRef(false);

  // Set while a click is entering the field, so the click's mouseup doesn't
  // collapse the select-all into a caret
  const selectOnClickRef = useRef(false);


 
  // Effect: Whenever we switch to editing mode, automatically focus the input
  // and select all text (exactly how Google Docs works when you click "Untitled Document").
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);


 
  /**
   * Validates, sanitizes, and saves the title.
   */
  const handleSave = () => {
    // 1. Trim leading/trailing whitespace
    let sanitizedTitle = tempTitle.trim();
   
    // 2. Sanitize: Remove illegal characters that might break file exports or URLs.
    // Removes: < > : " / \ | ? *
    sanitizedTitle = sanitizedTitle.replace(/[<>:"/\\|?*]/g, '');


    // 3. Fallback check: If the user deleted everything, revert to the default title.
    if (sanitizedTitle === '') {
      sanitizedTitle = defaultTitle;
    }


    // 4. Update states
    setCurrentTitle(sanitizedTitle);
    setTempTitle(sanitizedTitle); // Sync temp state with sanitized output
    setIsEditing(false);


    // 5. Fire callback to parent component (e.g., to trigger a backend API save)
    if (sanitizedTitle !== currentTitle) {
      onSave(sanitizedTitle);
    }
  };


 
  /**
   * Handles keyboard interactions while in the input field.
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur instead of saving inline: leaving the input focused keeps the browser's
      // text selection highlighted. onBlur runs handleSave, so Enter === clicking outside.
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Cancel edit: the blur handler reverts to the last saved title
      skipSaveRef.current = true;
      inputRef.current?.blur();
    }
  };


  /**
   * Exits edit mode: saves, unless the exit came from Escape.
   */
  const handleBlur = () => {
    selectOnClickRef.current = false;

    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      setTempTitle(currentTitle);
      setIsEditing(false);
      return;
    }
    handleSave();
  };


  /**
   * Activates edit mode.
   */
  const handleTextClick = () => {
    setIsEditing(true);
  };


  return (
    <div className={`relative inline-block ${className}`}>
      
        <input
          ref={inputRef}
          type="text"
          value={tempTitle}
          onChange={(e) => {
            
            if(e.target.value.length>50){

              toastMsg('error','Title must be within 50 characters')
              
              return
            }

            setTempTitle(e.target.value)}}
          onBlur={handleBlur} // Clicking completely outside the input triggers a save
          onKeyDown={handleKeyDown}
          placeholder={defaultTitle}
          // Base styles for the input + consumer overrides
          className={`rounded
            ${inputClassName}
          `}
          onFocus={(e) => {
            // Select all text on focus for easy replacement
            setIsEditing(true);
            selectOnClickRef.current = true;
            e.target.select();
          }}
          onMouseUp={(e) => {
            // The click that focused the input would otherwise place a caret
            // and drop the selection made in onFocus
            if (selectOnClickRef.current) {
              selectOnClickRef.current = false;
              e.preventDefault();
            }
          }}
          style={{
            // minWidth: '200px',
            width:'100%',
            height:'40px',
            fontSize:'var(--font-size-h2) !important',
            // Borderless by default: only the bottom edge shows while focused
            background:'transparent',
            border:'none',
            outline:'none',
            boxShadow:'none',
            borderBottom:isEditing ? '1px solid var(--ff-color-focus-ring-blue)' : '1px solid transparent'
            // Dynamically adjust width based on character count so it grows nicely
            // width: `${Math.max(tempTitle.length, defaultTitle.length)}ch`
          }}
        />
     
      
    </div>
  );
}




/**
 * Demo Component to show how to use InlineTitleEditor
 */
export function DemoUsage() {
  return (
    <div className="p-10 bg-gray-50 min-h-screen font-sans">
      <div className="mb-4 text-sm text-gray-500">
        Click the title below to edit it. Press <kbd className="bg-gray-200 px-1 rounded">Enter</kbd> or click outside to save. Press <kbd className="bg-gray-200 px-1 rounded">Esc</kbd> to cancel.
      </div>
     
      <div className="bg-white p-4 border rounded shadow-sm flex items-center">
        {/* USAGE EXAMPLE */}
        <InlineTitleEditor
          initialTitle="" // Starting empty to test fallback
          defaultTitle="Untitled Quiz"
          onSave={(newTitle) => console.log("Saved to database:", newTitle)}
          textClassName="text-xl text-[#1e3a8a]" // applying LYWO deep blue
          inputClassName="text-xl"
        />
      </div>
    </div>
  );
}
